import React, { useMemo } from 'react';

import COLORS from '../constants/colors';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const formatValue = (value) => {
  if (!Number.isFinite(value)) return '--';
  const rounded = Math.round((value + Number.EPSILON) * 100) / 100;
  return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(2);
};

const rangeInputStyle = {
  position: 'absolute',
  width: '100%',
  top: 0,
  bottom: 0,
  margin: 0,
  WebkitAppearance: 'none',
  background: 'transparent',
  pointerEvents: 'none',
};

const overrideThumbStyles = `
input[type="range"].filter-range::-webkit-slider-thumb {
   -webkit-appearance: none;
   appearance: none;
   height: 18px;
   width: 18px;
   border-radius: 50%;
   background: ${COLORS.MAIN_WHITE};
   border: 2px solid ${COLORS.MAIN_BLUE};
   box-shadow: 0 4px 12px rgba(0, 48, 102, 0.25);
   pointer-events: all;
}

input[type="range"].filter-range::-moz-range-thumb {
   height: 18px;
   width: 18px;
   border-radius: 50%;
   background: ${COLORS.MAIN_WHITE};
   border: 2px solid ${COLORS.MAIN_BLUE};
   box-shadow: 0 4px 12px rgba(0, 48, 102, 0.25);
   pointer-events: all;
}

input[type="range"].filter-range::-ms-thumb {
   height: 18px;
   width: 18px;
   border-radius: 50%;
   background: ${COLORS.MAIN_WHITE};
   border: 2px solid ${COLORS.MAIN_BLUE};
   box-shadow: 0 4px 12px rgba(0, 48, 102, 0.25);
   pointer-events: all;
}
`;

function FilterRange({
  label,
  min = 0,
  max = 10,
  step = 1,
  values,
  value,
  single = false,
  onChange,
  unit = 'm',
}) {
  const [low, high] = useMemo(() => {
    if (single) {
      const safeValue = clamp(Number(value ?? min), min, max);
      return [safeValue, safeValue];
    }
    const [providedLow, providedHigh] = Array.isArray(values)
      ? values
      : [min, max];
    const safeLow = clamp(Number(providedLow ?? min), min, max);
    const safeHigh = clamp(Number(providedHigh ?? max), min, max);
    if (safeLow > safeHigh) {
      return [safeHigh, safeHigh];
    }
    return [safeLow, safeHigh];
  }, [max, min, single, value, values]);

  const domain = Math.max(max - min, Number.EPSILON);
  const lowPercent = ((low - min) / domain) * 100;
  const highPercent = ((high - min) / domain) * 100;

  const handleLowChange = (event) => {
    const next = clamp(Number(event.target.value), min, high);
    const safeMaxLow = Math.max(min, high - step);
    const restricted = Math.min(next, safeMaxLow);
    const clamped = clamp(restricted, min, high);
    if (typeof onChange === 'function') {
      onChange([Number(formatValue(clamped)), Number(formatValue(high))]);
    }
  };

  const handleHighChange = (event) => {
    const next = clamp(Number(event.target.value), low, max);
    const safeMinHigh = Math.min(max, low + step);
    const restricted = Math.max(next, safeMinHigh);
    const clamped = clamp(restricted, low, max);
    if (typeof onChange === 'function') {
      onChange([Number(formatValue(low)), Number(formatValue(clamped))]);
    }
  };

  const handleSingleChange = (event) => {
    const next = clamp(Number(event.target.value), min, max);
    if (typeof onChange === 'function') {
      onChange(Number(formatValue(next)));
    }
  };

  const formattedLow = formatValue(low);
  const formattedHigh = formatValue(high);
  const formattedValue = single ? formatValue(low) : null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}>
      <style>{overrideThumbStyles}</style>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          color: COLORS.MAIN_BLUE,
        }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{label}</span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: COLORS.MAIN_GRAY,
          }}>
          {single
            ? `${formattedValue} ${unit}`
            : `${formattedLow} - ${formattedHigh} ${unit}`}
        </span>
      </div>
      <div style={{ position: 'relative', height: 32 }}>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: 6,
            borderRadius: 999,
            background: 'rgba(0, 48, 102, 0.12)',
            transform: 'translateY(-50%)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: `${single ? 0 : lowPercent}%`,
            right: `${single ? 100 - highPercent : 100 - highPercent}%`,
            height: 6,
            borderRadius: 999,
            background: COLORS.MAIN_BLUE,
            transform: 'translateY(-50%)',
          }}
        />
        <input
          type='range'
          min={min}
          max={max}
          step={step}
          value={single ? low : low}
          onChange={single ? handleSingleChange : handleLowChange}
          className='filter-range'
          style={{
            ...rangeInputStyle,
            zIndex: 3,
          }}
        />
        {!single && (
          <input
            type='range'
            min={min}
            max={max}
            step={step}
            value={high}
            onChange={handleHighChange}
            className='filter-range'
            style={{
              ...rangeInputStyle,
              zIndex: 4,
            }}
          />
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: COLORS.MAIN_GRAY,
        }}>
        <span>{`${formatValue(min)} ${unit}`}</span>
        <span>{`${formatValue(max)} ${unit}`}</span>
      </div>
    </div>
  );
}

export default FilterRange;
